import React, { ReactNode, useState } from 'react';
import { ErrorBoundaryTW } from '../errorBoundary/ErrorBoundaryTW';
import { Show } from '../../components/shared/Show';
import { HeaderText3TW, HeaderTextTW } from '../HeaderTextTW/HeaderTextTW';
import classNames from 'classnames';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import { IHasTestData } from '../../ui-toolkit/test-utilities/IHasTestData';
import { mergeTests } from '../../ui-toolkit/test-utilities/mergeTests';

export interface CardTWProps extends IHasTestData {
  /** Is this card collapsible */
  isCollapsible?: boolean;
  /** Does this card start collapsed */
  isCollapsed?: boolean;
  /** Execute function when card collapse/expand */
  onChangeCollapseState?: () => void;
  /** Heading text. */
  title?: ReactNode;
  /** Heading text or component. */
  footer?: ReactNode;
  /** Additional classname for card content. */
  contentClassName?: string;
  /** Additional classname for card title. */
  titleClassName?: string;
  children?: ReactNode;
  className?: string;
  /** Heading buttons. */
  titleActions?: ReactNode;
}

/*
 * In practice, this intended to be the sole "entry point" for a UI Card
 * The different implementations and their details are "hidden" from the developer.
 * The implementations are built with Tailwind and HeadlessUI
 * The component chooses which implementation based on props: isCollapsible
 * A collapsible Card (TransitionCard) can "fold up" or "retract".
 * The animation between the states of the TransitionCard can be improved! :-)
 * A collapsible Card has a "chevron" to indicate its state.
 * */
export const CardTW: React.FC<CardTWProps> = ({ isCollapsible, ...rest }) => {
  if (isCollapsible) {
    return <TransitionCard {...rest} />;
  }
  return <StaticCard {...rest} />;
};

const StaticCard: React.FC<CardTWProps> = ({
  children,
  footer,
  title,
  className,
  contentClassName,
  titleClassName,
  titleActions,
  ...rest
}) => {
  return (
    <ErrorBoundaryTW>
      <div
        className={classNames(' rounded-2xl bg-white shadow', className)}
        data-test-id={mergeTests(rest['data-test-id'], 'static-card-tw')}
        data-test-class={mergeTests(rest['data-test-class'], 'static-card-tw')}
      >
        <Show when={!!title}>
          <div className="flex flex-row">
            <HeaderText3TW className={classNames('w-full p-4 sm:px-6 sm:pt-6', titleClassName)}>{title}</HeaderText3TW>
            <Show when={!!titleActions}>{titleActions}</Show>
          </div>
        </Show>
        <div className={contentClassName || 'px-4 py-5 sm:px-6 sm:pb-6 sm:pt-1'}>{children}</div>
        <Show when={!!footer}>
          <div className="p-4 sm:px-6">{footer}</div>
        </Show>
      </div>
    </ErrorBoundaryTW>
  );
};

const TransitionCard: React.FC<CardTWProps> = ({
  children,
  footer,
  title,
  contentClassName,
  isCollapsed = false,
  onChangeCollapseState,
  titleActions,
  ...rest
}) => {
  const [isCardCollapsed, setIsCardCollapsed] = useState<boolean>(isCollapsed);
  return (
    <ErrorBoundaryTW>
      <div
        className={'overflow-hidden rounded-2xl bg-white shadow'}
        data-test-id={mergeTests(rest['data-test-id'], 'transition-card-tw')}
        data-test-class={mergeTests(rest['data-test-class'], 'transition-card-tw')}
      >
        <RotatingChevron
          isCardCollapsed={isCardCollapsed}
          setIsCardCollapsed={setIsCardCollapsed}
          title={title}
          onChangeCollapseState={onChangeCollapseState}
          titleActions={titleActions}
        />
        <Transition
          show={!isCardCollapsed}
          as={'div'}
          enter={'transition ease-out duration-150'}
          enterFrom={'transform opacity-0 scale-15'}
          enterTo={'transform opacity-100 scale-100'}
          leave={'transition ease-in duration-150'}
          leaveFrom={'transform opacity-100 scale-100'}
          leaveTo={'transform opacity-0 scale-15'}
        >
          <div className={contentClassName || 'px-4 py-5 sm:px-6 sm:pb-6 sm:pt-1'}>{children}</div>
          <Show when={!!footer}>
            <div className="px-4 py-4 sm:px-6">{footer}</div>
          </Show>
        </Transition>
      </div>
    </ErrorBoundaryTW>
  );
};

const RotatingChevron: React.FC<{
  isCardCollapsed: boolean;
  setIsCardCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  title?: React.ReactNode;
  onChangeCollapseState?: () => void;
  titleActions?: React.ReactNode;
}> = ({ isCardCollapsed, setIsCardCollapsed, title, onChangeCollapseState, titleActions }) => {
  const handleChangeCollapseState = () => {
    setIsCardCollapsed((prevState) => !prevState);
    if (onChangeCollapseState) {
      onChangeCollapseState();
    }
  };
  return (
    <div className={'flex flex-row items-center gap-4'}>
      <button className={`flex items-center px-4 py-4 sm:px-6 sm:pt-6`} onClick={handleChangeCollapseState}>
        <ChevronDownIcon
          className={classNames('h-7 w-7 flex-none transition duration-150 ease-in-out', {
            '-rotate-90': isCardCollapsed,
          })}
        />
        <HeaderTextTW>{title}</HeaderTextTW>
      </button>
      <Show when={!!titleActions}>{titleActions}</Show>
    </div>
  );
};
